import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import aoAPI from '../../api/aoAPI';

const fmt = (n) =>
  n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n) : '—';

const AODailyCollectionReport = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryCards, setSummaryCards] = useState({
    totalCash: 0,
    totalGCash: 0,
    totalBank: 0,
    grandTotal: 0,
  });

  // Load daily report
  const loadDailyReport = async (date) => {
    setLoading(true);
    try {
      const res = await aoAPI.getDailyReport({ date });
      const data = res.data || [];
      setReportData(data);

      // Compute summaries
      let totalCash = 0, totalGCash = 0, totalBank = 0;
      data.forEach(clientGroup => {
        totalCash += clientGroup.subtotalCash || 0;
        totalGCash += clientGroup.subtotalGCash || 0;
        totalBank += clientGroup.subtotalBank || 0;
      });
      const grandTotal = totalCash + totalGCash + totalBank;

      setSummaryCards({ totalCash, totalGCash, totalBank, grandTotal });
    } catch (err) {
      console.error('Failed to load daily report', err);
      setReportData([]);
      setSummaryCards({ totalCash: 0, totalGCash: 0, totalBank: 0, grandTotal: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Load report on mount and when date changes
  useEffect(() => {
    loadDailyReport(selectedDate);
  }, [selectedDate]);

  return (
    <Layout pageTitle="Daily Collection Report">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* Date Picker */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-700">Report Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Today
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <p className="text-xs text-gray-600 font-medium mb-1">Cash Total</p>
            <p className="text-2xl font-bold text-gray-800">₱{fmt(summaryCards.totalCash)}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <p className="text-xs text-gray-600 font-medium mb-1">GCash Total</p>
            <p className="text-2xl font-bold text-gray-800">₱{fmt(summaryCards.totalGCash)}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <p className="text-xs text-gray-600 font-medium mb-1">Bank Transfer Total</p>
            <p className="text-2xl font-bold text-gray-800">₱{fmt(summaryCards.totalBank)}</p>
          </div>
          <div className="rounded-lg border border-teal-200 bg-teal-50 p-5">
            <p className="text-xs text-teal-600 font-medium mb-1">Grand Total</p>
            <p className="text-2xl font-bold text-teal-700">₱{fmt(summaryCards.grandTotal)}</p>
          </div>
        </div>

        {/* Daily Collections by Client */}
        {!loading && reportData.length > 0 && (
          <div className="space-y-6">
            {reportData.map(clientGroup => (
              <div key={clientGroup.clientId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Client Header */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        {clientGroup.clientName}
                        {clientGroup.clientCode && (
                          <span className="text-xs font-normal text-gray-600 ml-2">
                            ({clientGroup.clientCode})
                          </span>
                        )}
                      </h4>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-700">
                        Total: <strong className="text-teal-600">₱{fmt(clientGroup.total)}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Collection Items Table */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-6 py-3 text-left font-medium text-gray-600">Time</th>
                      <th className="px-6 py-3 text-right font-medium text-gray-600">Amount</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-600">Payment Method</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-600">Reference No</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-600">Collected By</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-600">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientGroup.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-700">
                          {new Date(item.date).toLocaleTimeString('en-PH', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-6 py-3 text-right font-medium text-gray-700">
                          ₱{fmt(item.amount)}
                        </td>
                        <td className="px-6 py-3">
                          <span className="inline-block text-xs font-medium px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                            {item.paymentMethod}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-gray-600">{item.referenceNo || '—'}</td>
                        <td className="px-6 py-3 text-gray-700">{item.collectedBy}</td>
                        <td className="px-6 py-3 text-gray-600 text-xs">{item.remarks || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Client Subtotals */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm">
                  <div className="flex justify-end gap-8">
                    {clientGroup.subtotalCash > 0 && (
                      <span>
                        Cash: <strong>₱{fmt(clientGroup.subtotalCash)}</strong>
                      </span>
                    )}
                    {clientGroup.subtotalGCash > 0 && (
                      <span>
                        GCash: <strong>₱{fmt(clientGroup.subtotalGCash)}</strong>
                      </span>
                    )}
                    {clientGroup.subtotalBank > 0 && (
                      <span>
                        Bank: <strong>₱{fmt(clientGroup.subtotalBank)}</strong>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && reportData.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No collections recorded for {new Date(selectedDate).toLocaleDateString()}.
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-500">Loading report...</div>
        )}
      </div>
    </Layout>
  );
};

export default AODailyCollectionReport;
